import { FastForward, FastRewind, PlayArrow, Repeat, SkipPrevious, Stop } from '@material-ui/icons'
import { Dispatch, useEffect, useRef, useState } from 'react'
import { IGlobalState, GlobalAction, GlobalActionTypeList } from '../../states/GlobalState'
import { getPlayheadDataFromPosition, getPlayheadDataFromTime, getSecondsPerBar } from '../../states/utils'
import DynamicWidthNumberInput from '../DynamicWidthNumberInput'
import './index.scss'
import { RealtimeClient } from '../../services/realtime-client'
import { UbiverseEventTopicList } from '../../types'
import CONST from '../../consts'
import { convertSequenceToData } from './utils';
import { filterSequenceDataByRepeatRange, getSequenceForRepeatFromPagination, getSequenceFromPagination } from '../../utils/sequence'
import { Button, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@ubiquitel/ui'

interface UbiverseControlProps {
  state: IGlobalState
  dispatch: Dispatch<GlobalAction>
  realtimeClient: RealtimeClient
}

const UbiverseControl = ({ state, dispatch, realtimeClient }: UbiverseControlProps) => {
  const [time, setTime] = useState(0)
  const [timeRepeat, setTimeRepeat] = useState({ from: 0, to: 0 })
  const [fromToInSamples, setFromToInSamples] = useState<number[]>([])
  const [stagedData, setStagedData] = useState<{ targetId: string, data: number[] }[]>([])
  const [paginations, setPaginations] = useState<{ targetId: string, pagination: number, numOfElementsLeft?: number }[]>([])
  const [showSkipPrevious, setShowSkipPrevious] = useState(false)
  const interval = useRef<NodeJS.Timeout | null>(null)
  const currentTime = useRef<number>(new Date().getTime())
  const previousTime = useRef<number>(new Date().getTime())
  const normalizeRepeatValue = (num: number) => Math.max(1, Math.floor(num))
  
  // ack events
  useEffect(() => {
    // Data Changed
    realtimeClient.subscribeEvent(UbiverseEventTopicList.AckDataChanged, (payload) => {
      console.log('UbiverseControl:: Ack Data Changed', payload)
      dispatch({ type: GlobalActionTypeList.SetControlIsPlaying, payload: true })
      // Play is per-device: address it to whichever endpoint just acked its
      // data. An ack without a clientId (pre-per-device-play firmware) falls
      // back to broadcast play. Stop stays broadcast — see handleOnStopClick.
      const targetId = payload?.clientId
      realtimeClient.emitEvent(UbiverseEventTopicList.SetPlay, targetId ? { isPlaying: true, targetId } : { isPlaying: true })
    })

    // Play Changed
    realtimeClient.subscribeEvent(UbiverseEventTopicList.AckPlayChanged, (payload) => {
      console.log('UbiverseControl:: Ack Play Changed', payload)
      if (payload.isPlaying) {
        dispatch({ type: GlobalActionTypeList.SetControlIsPlaying, payload: true })
        // realtimeClient.emitEvent(UbiverseEventTopicList.SetPlay, { isPlaying: true })
      } else {
        dispatch({ type: GlobalActionTypeList.SetControlIsPlaying, payload: false })
        // realtimeClient.emitEvent(UbiverseEventTopicList.SetPlay, { isPlaying: false })
      }
    })

    // Buffer Updated
    realtimeClient.subscribeEvent(UbiverseEventTopicList.AckBufferUpdated, () => {
      console.log('UbiverseControl:: Ack Buffer Updated', state.control)    
      if (state.control.repeat?.isActive) {
        console.log('UbiverseControl:: Ack Buffer Updated - Repeat', stagedData)         
        stagedData.forEach((item, index) => {
          const repeatRangeData = fromToInSamples.length >= 4
            ? filterSequenceDataByRepeatRange(item.data, fromToInSamples[1], fromToInSamples[3])
            : item.data;
          const repeatDurationInBars = (state.control.repeat.to.bar - state.control.repeat.from.bar)
            + (state.control.repeat.to.beat - state.control.repeat.from.beat) / state.control.signature.numerator
          const repeatDurationSeconds = repeatDurationInBars * getSecondsPerBar(state.control.bpm, state.control.signature)
          const { data, pagination, numOfElementsLeft } = getSequenceForRepeatFromPagination(
            repeatRangeData,
            paginations.find(pagination => pagination.targetId === item.targetId)?.pagination || 0,
            paginations.find(pagination => pagination.targetId === item.targetId)?.numOfElementsLeft || 0,
            // convert bar/beat to samples during from to to of repeat
            repeatDurationSeconds / CONST.sampleDelay.secondsPerSample,
            CONST.bufferSize
          )

          const newPagination = pagination

          realtimeClient.emitEvent(UbiverseEventTopicList.SetData, { targetId: item.targetId, data })
          setPaginations(prevPaginations => prevPaginations.map(pagination => pagination.targetId === item.targetId ? { ...pagination, pagination: newPagination, numOfElementsLeft } : pagination))
        })
      } else {
        stagedData.forEach((item, index) => {
          realtimeClient.emitEvent(UbiverseEventTopicList.SetData, { targetId: item.targetId, data: getSequenceFromPagination(item.data, paginations.find(pagination => pagination.targetId === item.targetId)?.pagination || 0) })
          setPaginations(prevPaginations => prevPaginations.map(p => p.targetId === item.targetId ? { ...p, pagination: p.pagination + 1 } : p))
        })
      }
    })

    // Play done.
    realtimeClient.subscribeEvent(UbiverseEventTopicList.AckPlayDone, () => {
      handleOnStopClick()
      setPaginations(stagedData.map(item => ({ targetId: item.targetId, pagination: 0 })))
      setStagedData([])
    })
  }, [realtimeClient, state.control, paginations])

  useEffect(() => {
    const fromToInSamples = convertSequenceToData([
      { id: 'from', intensity: 1, position: state.control.signature.numerator * (state.control.repeat?.from.bar - 1) + (state.control.repeat?.from.beat - 1) / state.control.signature.numerator },
      { id: 'to', intensity: 1, position: state.control.signature.numerator * (state.control.repeat?.to.bar - 1) + (state.control.repeat?.to.beat - 1) / state.control.signature.numerator }
    ], state.control.playheadTime, state.control.signature.numerator, state.control.bpm)
    setFromToInSamples(fromToInSamples)
  }, [state.control.repeat, state.control.signature, state.control.bpm])
  
  useEffect(() => {
    const playheadData = getPlayheadDataFromTime(time, state.control.bpm, state.control.signature)
    dispatch({
      type: GlobalActionTypeList.SetControlPlayheadTime,
      payload: playheadData.playheadTime
    })
    dispatch({
      type: GlobalActionTypeList.SetControlPlayheadPosition,
      payload: {
        bar: playheadData.bar,
        beat: playheadData.beat
      }
    })
  }, [time, state.control.signature, state.control.bpm])

  useEffect(() => {
    const repeatFrom = getPlayheadDataFromTime(timeRepeat.from, state.control.bpm, state.control.signature)
    const repeatTo = getPlayheadDataFromTime(timeRepeat.to, state.control.bpm, state.control.signature)
    dispatch({
      type: GlobalActionTypeList.SetControlRepeatFrom,
      payload: { bar: repeatFrom.bar, beat: repeatFrom.beat }
    })
    dispatch({
      type: GlobalActionTypeList.SetControlRepeatTo,
      payload: { bar: repeatTo.bar, beat: repeatTo.beat }
    })
  }, [timeRepeat, state.control.signature, state.control.bpm])

  useEffect(() => {
    if (!state.control.isPlaying) {
      setTime(state.control.playheadTime)
    }
  }, [state.control.playheadTime, state.control.isPlaying])

  useEffect(() => {
    // if playing, set playhead time in infinite loop
    if (state.control.isPlaying) {
      currentTime.current = new Date().getTime()
      previousTime.current = new Date().getTime()

      if (state.control.repeat?.isActive) {
        setTime(timeRepeat.from)
      } else {
        setTime(state.control.playheadTime)
      }

      interval.current = setInterval(() => {
        currentTime.current = new Date().getTime()
        setTime(prevTime => {
          if (state.control.repeat?.isActive) {
            if (prevTime >= timeRepeat.to) {
              return timeRepeat.from
            }
          }
          return prevTime + ((currentTime.current - previousTime.current)/1000)
        })
        previousTime.current = currentTime.current
      })
    } else {
      if (interval.current) {
        clearInterval(interval.current)
        interval.current = null
      }
    }
  }, [state.control.isPlaying])

  useEffect(() => {
    setShowSkipPrevious(state.control.playheadTime > 0 && !state.control.isPlaying)
  }, [state.control.playheadTime, state.control.isPlaying])

  useEffect(() => {
    if (stagedData.length > 0) {

      // Repeating
      if (state.control.repeat?.isActive) {
        console.log("Repeat set data begin")

        stagedData.forEach((item, index) => {
          console.log("Repeat set data begin sending data to server")
          // realtimeClient.emitEvent(UbiverseEventTopicList.SetData, { targetId: item.targetId, data: item.data })
          console.log('creating data set for item', item.targetId)
          const repeatRangeData = fromToInSamples.length >= 4
            ? filterSequenceDataByRepeatRange(item.data, fromToInSamples[1], fromToInSamples[3])
            : item.data;
          const repeatDurationInBars = (state.control.repeat.to.bar - state.control.repeat.from.bar)
            + (state.control.repeat.to.beat - state.control.repeat.from.beat) / state.control.signature.numerator
          const repeatDurationSeconds = repeatDurationInBars * getSecondsPerBar(state.control.bpm, state.control.signature)
          const { data, pagination, numOfElementsLeft } = getSequenceForRepeatFromPagination(
            // only items between defined range of _fromToInSamples
            repeatRangeData,
            paginations.find(pagination => pagination.targetId === item.targetId)?.pagination || 0,
            paginations.find(pagination => pagination.targetId === item.targetId)?.numOfElementsLeft || 0,
            // convert bar/beat to samples during from to to of repeat
            repeatDurationSeconds / CONST.sampleDelay.secondsPerSample,
            CONST.bufferSize * 2
          )

          console.log(data, 'data to be sent')

          const newPagination = pagination

          console.log('event dispatched!')
          realtimeClient.emitEvent(UbiverseEventTopicList.SetData, { targetId: item.targetId, data })
          setPaginations(prevPaginations => prevPaginations.map(pagination => pagination.targetId === item.targetId ? { ...pagination, pagination: newPagination, numOfElementsLeft } : pagination))
        })

      // Not repeating
      } else {
        stagedData.forEach((item, index) => {
          realtimeClient.emitEvent(UbiverseEventTopicList.SetData, { targetId: item.targetId, data: item.data.slice(0, CONST.bufferSize * 2) })
          setPaginations(prevPaginations => prevPaginations.map(pagination => pagination.targetId === item.targetId ? { ...pagination, pagination: pagination.pagination + (Math.ceil(item.data.length / CONST.bufferSize) > 2 ? 2 : Math.ceil(item.data.length / CONST.bufferSize)) } : pagination))

          console.log( Math.ceil(item.data.length / CONST.bufferSize), ' Math.ceil(item.data.length / CONST.bufferSize)')
        })
      }

     
    }
  }, [stagedData])

  // Event Handlers
  // Resolve the realtime targetId from a SimulatorNode's selectedClientId
  const resolveTargetId = (edgeTarget: string): string | null => {
    const node = state.nodes.find(n => n.id === edgeTarget);
    if (node?.type === 'simulatorNode') {
      // A SimulatorNode with no device selected is not a destination — return
      // null so the caller drops the edge and we don't broadcast data addressed
      // to a meaningless id (every device would otherwise ignore it anyway, but
      // this avoids the wasted send entirely).
      return node.data?.selectedClientId ? (node.data.selectedClientId as string) : null;
    }
    return edgeTarget;
  }

  // Play
  const handleOnPlayClick = () => {
    if (state.control.isPlaying) {
      setTime(0)
    } else {
      const resolvedEdges = state.edges
        .map(edge => ({ ...edge, resolvedTargetId: resolveTargetId(edge.target) }))
        .filter(edge => edge.resolvedTargetId !== null);
      setStagedData(resolvedEdges.map(edge => ({ targetId: edge.resolvedTargetId!, data: convertSequenceToData(state.sequences.find(s => s.id === edge.source)?.notes || [], state.control.playheadTime, state.control.signature.numerator, state.control.bpm) })))
      setPaginations(resolvedEdges.map(edge => ({ targetId: edge.resolvedTargetId!, pagination: 0 })))
    }
  }
  // Stop
  const handleOnStopClick = () => {
    if (showSkipPrevious) {
      setTime(0)
    } else {
      // Deliberately unaddressed: broadcast stop still reaches an endpoint
      // whose node selection changed mid-play, so nothing keeps vibrating.
      realtimeClient.emitEvent(UbiverseEventTopicList.SetPlay, { isPlaying: false })
      dispatch({ type: GlobalActionTypeList.SetControlIsPlaying, payload: false })
    }
  }

  // Fast Forward
  const handleOnFastForwardClick = () => {
    const skipTime = getSecondsPerBar(state.control.bpm, state.control.signature) * 2
    setTime(prevTime => prevTime + skipTime)
  }

  // Fast Rewind
  const handleOnFastRewindClick = () => {
    const skipTime = getSecondsPerBar(state.control.bpm, state.control.signature) * 2
    setTime(prevTime => prevTime - skipTime > 0 ? prevTime - skipTime : 0)
  }

  return (
    <div className="component--ubiverse-control">
      {/* Row 1: Parameters (numbers) + Button Controllers (CTAs) */}
      <div className="component--ubiverse-control__row">
        {/* Parameters */}
        <div className="component--ubiverse-control__parameters">
          <div className="component--ubiverse-control__parameter">
            <DynamicWidthNumberInput
              value={state.control.playheadPosition.bar}
              onChange={(num) => {
                const playheadData = getPlayheadDataFromPosition(num, 1, state.control.bpm, state.control.signature)
                setTime(playheadData.playheadTime)
              }}
            />
            <p className="component--ubiverse-control__parameter__label">Bar</p>
          </div>
          <div className="component--ubiverse-control__parameter">
            <DynamicWidthNumberInput
              value={state.control.playheadPosition.beat}
              onChange={(num) => {
                const playheadData = getPlayheadDataFromPosition(state.control.playheadPosition.bar, num, state.control.bpm, state.control.signature)
                setTime(playheadData.playheadTime)
              }}
            />
            <p className="component--ubiverse-control__parameter__label">Beat</p>
          </div>
          <div className="component--ubiverse-control__parameter__separator">|</div>
          <div className="component--ubiverse-control__parameter">
            <DynamicWidthNumberInput
              value={state.control.bpm}
              onChange={(num) => {
                dispatch({ type: GlobalActionTypeList.SetControlBpm, payload: num })
                const playheadData = getPlayheadDataFromPosition(state.control.playheadPosition.bar, state.control.playheadPosition.beat, num, state.control.signature)
                setTime(playheadData.playheadTime)
              }}
            />
            <p className="component--ubiverse-control__parameter__label">Tempo</p>
          </div>
          <div className="component--ubiverse-control__parameter__separator">|</div>
          <div className="component--ubiverse-control__parameter">
            <div className="component--ubiverse-control__parameter__signature">
              <DynamicWidthNumberInput
                value={state.control.signature.numerator}
                onChange={(num) => {
                  dispatch({ type: GlobalActionTypeList.SetControlSignatureNumerator, payload: num })
                }}
              />
              <p className="component--ubiverse-control__parameter__value">/</p>
              <DynamicWidthNumberInput
                value={state.control.signature.denominator}
                onChange={(num) => {
                  dispatch({ type: GlobalActionTypeList.SetControlSignatureDenominator, payload: num })
                }}
              />
            </div>
            <p className="component--ubiverse-control__parameter__label">Signature</p>
          </div>
        </div>

        {/* Button Controllers */}
        <TooltipProvider>
          <div className="component--ubiverse-control__buttons">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" iconOnly className="component--ubiverse-control__button" onClick={() => { handleOnFastRewindClick() }}>
                  <FastRewind className="component--ubiverse-control__button__icon" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Rewind</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" iconOnly className="component--ubiverse-control__button" onClick={() => { handleOnFastForwardClick() }}>
                  <FastForward className="component--ubiverse-control__button__icon" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Fast forward</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" iconOnly className="component--ubiverse-control__button" onClick={() => { handleOnStopClick() }}>
                  {showSkipPrevious ? (
                    <SkipPrevious className="component--ubiverse-control__button__icon" />
                  ) : (
                    <Stop className="component--ubiverse-control__button__icon" />
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent>{showSkipPrevious ? 'Return to start' : 'Stop'}</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  iconOnly
                  className={`component--ubiverse-control__button ${state.control.isPlaying ? 'playing' : ''}`}
                  onClick={() => { handleOnPlayClick() }}
                >
                  <PlayArrow className="component--ubiverse-control__button__icon" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Play</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  iconOnly
                  className={`component--ubiverse-control__button ${state.control.repeat?.isActive ? 'repeat-active' : ''}`}
                  onClick={() => { dispatch({ type: GlobalActionTypeList.SetControlRepeatIsActive, payload: !state.control.repeat?.isActive }) }}
                >
                  <Repeat className="component--ubiverse-control__button__icon" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Repeat</TooltipContent>
            </Tooltip>
          </div>
        </TooltipProvider>
      </div>

      {/* Row 2: Repeat settings */}
      {state.control.repeat?.isActive && (
        <div className="component--ubiverse-control__row component--ubiverse-control__repeat-row">
          <div className="component--ubiverse-control__parameters">
            <p className="component--ubiverse-control__repeat-label">Repeat</p>
            {/* From */}
            <div className="component--ubiverse-control__parameter">
              <DynamicWidthNumberInput
                small
                value={state.control.repeat.from.bar}
                min={1}
                step={1}
                onChange={(num) => { setTimeRepeat({
                  from: getPlayheadDataFromPosition(normalizeRepeatValue(num), 1, state.control.bpm, state.control.signature).playheadTime,
                  to: getPlayheadDataFromPosition(normalizeRepeatValue(num), 1, state.control.bpm, state.control.signature).playheadTime < timeRepeat.to
                    ? timeRepeat.to
                    : getPlayheadDataFromPosition(normalizeRepeatValue(num), 1, state.control.bpm, state.control.signature).playheadTime
                  })}}
              />
              <p className="component--ubiverse-control__parameter__label small">Bar</p>
            </div>
            <div className="component--ubiverse-control__parameter">
              <DynamicWidthNumberInput
                small
                value={state.control.repeat.from.beat}
                min={1}
                step={1}
                onChange={(num) => { setTimeRepeat({
                  from: getPlayheadDataFromPosition(state.control.repeat.from.bar, normalizeRepeatValue(num), state.control.bpm, state.control.signature).playheadTime,
                  to: getPlayheadDataFromPosition(state.control.repeat.from.bar, normalizeRepeatValue(num), state.control.bpm, state.control.signature).playheadTime < timeRepeat.to
                    ? timeRepeat.to
                    : getPlayheadDataFromPosition(state.control.repeat.from.bar, normalizeRepeatValue(num), state.control.bpm, state.control.signature).playheadTime
                  })}}
              />
              <p className="component--ubiverse-control__parameter__label small">Beat</p>
            </div>
            {/* To */}
            <div className="component--ubiverse-control__parameter">
              <p className="component--ubiverse-control__parameter__label">-</p>
            </div>
            <div className="component--ubiverse-control__parameter">
              <DynamicWidthNumberInput
                small
                value={state.control.repeat.to.bar}
                min={1}
                step={1}
                onChange={(num) => { setTimeRepeat({
                  from: getPlayheadDataFromPosition(normalizeRepeatValue(num), 1, state.control.bpm, state.control.signature).playheadTime < timeRepeat.from
                    ? getPlayheadDataFromPosition(normalizeRepeatValue(num), 1, state.control.bpm, state.control.signature).playheadTime
                    : timeRepeat.from,
                  to: getPlayheadDataFromPosition(normalizeRepeatValue(num), 1, state.control.bpm, state.control.signature).playheadTime
                })}}
              />
              <p className="component--ubiverse-control__parameter__label small">Bar</p>
            </div>
            <div className="component--ubiverse-control__parameter">
              <DynamicWidthNumberInput
                small
                value={state.control.repeat.to.beat}
                min={1}
                step={1}
                onChange={(num) => { setTimeRepeat({
                  from: getPlayheadDataFromPosition(state.control.repeat.to.bar, normalizeRepeatValue(num), state.control.bpm, state.control.signature).playheadTime < timeRepeat.from
                    ? getPlayheadDataFromPosition(state.control.repeat.to.bar, normalizeRepeatValue(num), state.control.bpm, state.control.signature).playheadTime
                    : timeRepeat.from,
                    to: getPlayheadDataFromPosition(state.control.repeat.to.bar, normalizeRepeatValue(num), state.control.bpm, state.control.signature).playheadTime
                  })}}
              />
              <p className="component--ubiverse-control__parameter__label small">Beat</p>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default UbiverseControl
